// Library Methods
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IshiharaResult from "./IshiharaResult/IshiharaResult";
import Alert from "src/components/shared/alerts/Alert/Alert";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Constants
import { ColorVisionDeficiencyType, examCardEnums } from "src/constants/enums";
import { IshiharaStateResponseType } from "src/services/exams/types";

// Utils
import { isTerminatedExamReport } from "src/utils/exams/examReportsHelper";

const IshiharaGraph = () => {
  const { t } = useTranslation();

  const { exam } = useContext(ReportContext);
  const { examReport } = useContext(ReportSectionContext);

  const isTerminated = useMemo(
    () =>
      isTerminatedExamReport({
        exam,
        examReport,
        sectionName: examCardEnums.COLOR_VISION.sectionName,
      }),
    [exam, examReport]
  );

  const colorVisionResults = useMemo(
    () => examReport?.state?.responses,
    [examReport?.state?.responses]
  );

  const deficiencyType = useMemo(
    () => examReport?.postProcessing?.colorVisionDeficiencyType ?? "",
    [examReport?.postProcessing?.colorVisionDeficiencyType]
  );

  const responseDistributionKeys: Array<string> = useMemo(
    () => Object.keys(examReport?.postProcessing?.responseDistribution ?? {}),
    [examReport?.postProcessing?.responseDistribution]
  );

  return (
    <Grid container sx={{ gap: "20px", height: "fit-content" }}>
      {isTerminated && (
        <Alert
          variant="error"
          alertContent={t("Terminated Exam")}
          props={{ width: "100%" }}
        />
      )}

      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "fit-content",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "4px",
            border: "1px solid #000",
            width: "100%",
            height: "fit-content",
          }}
        >
          <Typography
            sx={{
              background: "#000",
              color: "#FFF",
              padding: "8px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {t("Color Vision Deficiency Results")}
          </Typography>
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "8px",
              height: "fit-content",
            }}
          >
            <Grid display="flex" gap="8px">
              <Typography fontSize="14px" fontWeight={600}>
                {t("Deficiency Type")}:
              </Typography>
              <Typography fontSize="14px">{t(deficiencyType)}</Typography>
            </Grid>

            {responseDistributionKeys?.length > 0 && (
              <Grid display="flex" gap="8px">
                <Typography fontSize="14px" fontWeight={600}>
                  {t("Response Distribution")}:
                </Typography>

                <Grid item xs display="flex" flexWrap="wrap" columnGap="16px">
                  {responseDistributionKeys?.map((key: string) => (
                    <Typography
                      key={`response-distribution-${key}`}
                      fontSize="14px"
                    >
                      {t(key)}:{" "}
                      {t(
                        examReport?.postProcessing?.responseDistribution?.[
                          key as ColorVisionDeficiencyType
                        ] ?? ""
                      )}
                    </Typography>
                  ))}
                </Grid>
              </Grid>
            )}

            <Divider color="#5B6D7A" sx={{ my: "16px" }} />

            <Grid
              sx={{
                display: "flex",
                flexWrap: "wrap",
                rowGap: "16px",
                height: "fit-content",
              }}
            >
              {!colorVisionResults || !colorVisionResults?.length ? (
                <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
                  {t("Error: No patient responses recorded.")}
                </Typography>
              ) : (
                colorVisionResults?.map((colorVisionResult, resultIndex) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={`ishihara-result-${resultIndex}`}
                    sx={{ height: "fit-content" }}
                  >
                    <IshiharaResult
                      colorVisionResult={
                        colorVisionResult as IshiharaStateResponseType
                      }
                      resultIndex={resultIndex}
                    />
                  </Grid>
                ))
              )}
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export default IshiharaGraph;
